import React, { useState, useEffect, useContext } from 'react';
import { Card, Table, Badge, Button, Form, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiFilter, FiPlus, FiDollarSign, FiArrowRight } from 'react-icons/fi';

import { AppContext } from '../AppContext';
import { tre } from '../trebus';

function Rates() {
    const [rates, setRates] = useState([]);
    const [filteredRates, setFilteredRates] = useState([]);
    const [searchString, setSearchString] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const context = useContext(AppContext);
    const navigate = useNavigate();

    // Load rates
    const loadRates = () => {
        setIsLoading(true);
        if (context?.user?.company_id) {
            tre.api('GET', `/companies/${context.user.company_id}/rates`, {
                context: context,
                success: (r) => {
                    setRates(r);
                    setIsLoading(false);
                },
                error: () => setIsLoading(false)
            });
        }
    };

    useEffect(() => {
        loadRates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [context?.user?.company_id]);

    // Filter rates when search changes
    useEffect(() => {
        let filtered = rates;

        if (searchString) {
            const search = searchString.toLowerCase();
            filtered = filtered.filter(r =>
                (r.name && r.name.toLowerCase().includes(search)) ||
                (r.start?.name && r.start.name.toLowerCase().includes(search)) ||
                (r.end?.name && r.end.name.toLowerCase().includes(search))
            );
        }

        setFilteredRates(filtered);
    }, [rates, searchString]);

    const onSelect = (rate) => {
        navigate(`/rates/${rate.id}`);
    };

    // -----| Render |-----
    return (
        <div className="container py-4">
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h4 className="mb-1" style={{ color: '#202124', fontWeight: 500 }}>
                        <FiDollarSign className="me-2" style={{ color: '#1a73e8' }} />
                        Tarifas
                    </h4>
                    <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                        {filteredRates.length} tarifas en total
                    </p>
                </div>
                <Button
                    variant="primary"
                    onClick={() => navigate('/rates/create')}
                >
                    <FiPlus className="me-2" />
                    Nueva Tarifa
                </Button>
            </div>

            {/* Filters - Inline style */}
            <div className="d-flex flex-wrap align-items-center gap-3 mb-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                {/* Search */}
                <div className="position-relative" style={{ minWidth: '280px', flex: 1, maxWidth: '400px' }}>
                    <FiSearch
                        className="position-absolute"
                        style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#5f6368' }}
                    />
                    <Form.Control
                        type="text"
                        placeholder="Buscar por nombre o parada..."
                        value={searchString}
                        onChange={(e) => setSearchString(e.target.value)}
                        style={{ paddingLeft: '38px', backgroundColor: 'white' }}
                    />
                </div>

                {/* Clear filters - only show if filters active */}
                {searchString && (
                    <Button
                        variant="link"
                        size="sm"
                        onClick={() => setSearchString('')}
                        style={{ color: '#ea4335', textDecoration: 'none' }}
                    >
                        <FiFilter className="me-1" /> Limpiar
                    </Button>
                )}
            </div>

            {/* Table Card */}
            <Card>
                {/* Top Info */}
                {!isLoading && filteredRates.length > 0 && (
                    <Card.Header className="d-flex justify-content-between align-items-center py-3" style={{ backgroundColor: '#f8f9fa' }}>
                        <span style={{ fontSize: '14px', color: '#5f6368' }}>
                            <strong style={{ color: '#202124' }}>{filteredRates.length}</strong> tarifas
                        </span>
                    </Card.Header>
                )}

                <Card.Body className="p-0">
                    {isLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" style={{ color: '#1a73e8' }} />
                            <p className="mt-3 text-muted">Cargando tarifas...</p>
                        </div>
                    ) : filteredRates.length > 0 ? (
                        <Table hover responsive className="mb-0">
                            <thead style={{ backgroundColor: '#f8f9fa' }}>
                                <tr>
                                    <th className="py-3 px-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>NOMBRE</th>
                                    <th className="py-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>RECORRIDO</th>
                                    <th className="py-3 text-end" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>PRECIO/VIAJE</th>
                                    <th className="py-3 px-3 text-end" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>PRECIO/MES</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRates.map((rate) => (
                                    <tr
                                        key={rate.id}
                                        onClick={() => onSelect(rate)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td className="py-3 px-3">
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="d-flex align-items-center justify-content-center rounded-circle me-2"
                                                    style={{
                                                        width: '32px',
                                                        height: '32px',
                                                        backgroundColor: '#e6f4ea',
                                                        color: '#34a853'
                                                    }}
                                                >
                                                    <FiDollarSign size={16} />
                                                </div>
                                                <span style={{ color: '#202124', fontWeight: 500 }}>
                                                    {rate.name || 'Sin nombre'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="py-3">
                                            <span className="d-inline-flex align-items-center text-muted" style={{ fontSize: '14px' }}>
                                                {rate.start ? rate.start.name : '?'}
                                                <FiArrowRight className="mx-2" size={12} />
                                                {rate.end ? rate.end.name : '?'}
                                            </span>
                                        </td>
                                        <td className="py-3 text-end">
                                            <Badge
                                                bg="info"
                                                style={{ fontSize: '12px', padding: '6px 12px', fontWeight: 500 }}
                                            >
                                                {rate.price} € / viaje
                                            </Badge>
                                        </td>
                                        <td className="py-3 px-3 text-end">
                                            <Badge
                                                bg="success"
                                                style={{ fontSize: '12px', padding: '6px 12px', fontWeight: 500 }}
                                            >
                                                {rate.price_month} € / mes
                                            </Badge>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : (
                        <div className="text-center py-5">
                            <FiDollarSign size={48} className="mb-3" style={{ color: '#dadce0' }} />
                            <p className="text-muted">No se encontraron tarifas</p>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
}

export default Rates;
